export function getWeekdayName(date: Date = new Date(), timezone?: string): string {
  if (timezone) {
    return date.toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone });
  }
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

/**
 * Converts a date string to JS Date.
 * Supports both "dd-mm-yyyy" (legacy) and "YYYY-MM-DD" (ISO) formats.
 */
export function convertToDate(dateString: string): Date {
  const parts = dateString.split('-').map(Number);

  if (parts.length !== 3 || parts.some(isNaN)) {
    throw new Error(
      `Invalid date format. Expected "dd-mm-yyyy" or "YYYY-MM-DD", received: ${dateString}`
    );
  }

  // Detect ISO format: first segment is a 4-digit year
  if (parts[0] > 31) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  const [day, month, year] = parts;
  return new Date(year, month - 1, day);
}

/**
 * Converts a JS Date to "YYYY-MM-DD" string (ISO date format).
 */
export function convertToString(rawDate: Date | string = new Date()): string {
  let date: Date;

  if (typeof rawDate === 'string') {
    date = new Date(rawDate);
  } else {
    date = rawDate;
  }

  const year: number = date?.getFullYear();
  const month: string = String(date?.getMonth() + 1).padStart(2, '0');
  const day: string = String(date?.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get human-readable label for a weekday (Today, Tomorrow, or day name)
 */
export function getWeekdayLabel(weekday: string, referenceDate: Date = new Date()): string {
  const today = getWeekdayName(referenceDate);
  
  if (weekday === today) {
    return 'Today';
  }

  const tomorrow = new Date(referenceDate);
  tomorrow.setDate(referenceDate.getDate() + 1);
  const tomorrowName = getWeekdayName(tomorrow);
  
  if (weekday === tomorrowName) {
    return 'Tomorrow';
  }

  return weekday;
}

/**
 * Get human-readable label for a specific date (Today, Tomorrow, or "Day, Month Date")
 * @param targetDate The date to get the label for
 * @param referenceDate The reference date (usually today)
 * @param timezone Optional timezone
 */
export function getDateLabel(
  targetDate: Date,
  referenceDate: Date = new Date(),
  timezone?: string
): string {
  // Normalize dates to midnight for comparison
  const normalizeDate = (d: Date): Date => {
    const result = new Date(d);
    result.setHours(0, 0, 0, 0);
    return result;
  };

  const normalizedTarget = normalizeDate(targetDate);
  const normalizedRef = normalizeDate(referenceDate);
  
  const diffTime = normalizedTarget.getTime() - normalizedRef.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  }
  
  if (diffDays === 1) {
    return 'Tomorrow';
  }

  // Format as "Day, Month Date" (e.g., "Friday, Dec 26")
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  };
  
  if (timezone) {
    options.timeZone = timezone;
  }

  return targetDate.toLocaleDateString('en-US', options);
}